import React, { useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import {
  Server,
  RotateCw,
  ArrowLeft,
  Wrench,
  Database,
  MessageSquare,
  AlertTriangle,
  FolderOpen,
  CheckCircle2,
  XCircle,
  Search,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "../../../ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { GatewayServersApi, GatewayPostProcessorApi } from "../Api/gatewayApi";
import { timeAgo, apiError } from "../utils/gatewayUtils";
import { useToast } from "../../../../hooks/use-toast";
import GatewaySensitivityBadge, { capabilitySensitivity } from "../components/GatewaySensitivityBadge";
import "../../ManagementDialog.css";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import "../AgenticGateway.css";

function priorityFilter<T>(items: T[], query: string, getName: (item: T) => string): T[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter((item) => getName(item).toLowerCase().startsWith(q));
}


interface ServerInfo {
  configKey: string;
  serverName?: string;
  configName?: string;
  status: string;
  active: boolean;
  toolCount?: number;
  resourceCount?: number;
  promptCount?: number;
  serverVersion?: string;
  version?: string;
  connectedAt?: string;
  lastActivity?: string;
}

interface SchemaProp {
  type?: string;
  description?: string;
}

interface InputSchema {
  properties?: Record<string, SchemaProp>;
  required?: string[];
}

interface Tool {
  name: string;
  description?: string;
  inputSchema?: InputSchema;
}

interface Resource {
  name: string;
  description?: string;
  uri?: string;
  mimeType?: string;
}

interface PromptArg {
  name: string;
  required?: boolean;
  description?: string;
}

interface Prompt {
  name: string;
  description?: string;
  arguments?: PromptArg[];
}

interface EntitySensitivity {
  serversByName?: Record<string, string>;
  tools?: any[];
}


function parseServerName(raw: string): { primary: string; sub?: string } {
  const trimmed = (raw || "unknown").trim();
  const match = trimmed.match(/^(.*?)(\s*\(([^)]+)\))$/);
  if (!match) return { primary: trimmed };
  return {
    primary: (match[1] || "").trim() || trimmed,
    sub: (match[3] || "").trim(),
  };
}


function ServerNameDisplay({ name }: { name: string }) {
  const { primary, sub } = parseServerName(name);
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <span className="truncate">{primary}</span>
      {sub && <span className="text-xs font-normal text-muted-foreground shrink-0">({sub})</span>}
    </span>
  );
}

function ConnectionBadge({ active }: { active: boolean }) {
  return (
    <span className="ag-chip" data-tone={active ? "success" : "danger"}>
      <span className="ag-status-dot" aria-hidden="true" />
      {active ? "Connected" : "Disconnected"}
    </span>
  );
}

function ServerSearch({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <div className="ag-search">
      <Search aria-hidden="true" />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function TabShell({ tabs, actions, children }: { tabs: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <>
      <div className="ag-card ag-toolbar ag-nav">
        {tabs}
        {actions}
      </div>
      <div className="ag-card ag-content">
        <div className="ag-scroll">{children}</div>
      </div>
    </>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="ag-loading">
      <span className="ag-spinner animate-spin" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function SchemaTable({ schema }: { schema: InputSchema }) {
  const props = schema.properties || {};
  const required = schema.required || [];
  const entries = Object.entries(props);
  if (entries.length === 0) return null;

  return (
    <div className="ag-table-wrap">
      <Table>
        <TableHeader>
          <TableRow className="border-border/60 hover:bg-transparent">
            <TableHead className="h-9 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Property</TableHead>
            <TableHead className="h-9 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Type</TableHead>
            <TableHead className="h-9 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Required</TableHead>
            <TableHead className="h-9 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map(([name, prop]) => (
            <TableRow key={name} className="border-border/50 hover:bg-primary/[0.025]">
              <TableCell className="py-2.5 font-mono text-xs text-primary">{name}</TableCell>
              <TableCell className="py-2.5 text-xs text-amber-600 dark:text-amber-400">{prop.type || "any"}</TableCell>
              <TableCell className="py-2.5">
                {required.includes(name) ? (
                  <span className="ag-chip" data-tone="danger">Required</span>
                ) : null}
              </TableCell>
              <TableCell className="py-2.5 text-xs text-muted-foreground">{prop.description || ""}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ToolFieldsDialog({ tool, onClose }: { tool: Tool | null; onClose: () => void }) {
  const open = !!tool;
  const entries = Object.entries(tool?.inputSchema?.properties ?? {});
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-2xl">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <Wrench />
            </span>
            <div>
              <DialogTitle className="font-mono">{tool?.name ?? "Tool"}</DialogTitle>
              <DialogDescription>
                {tool?.description || "No description"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          {entries.length === 0 ? (
            <p className="ag-row-text">This tool takes no parameters.</p>
          ) : (
            tool?.inputSchema && <SchemaTable schema={tool.inputSchema} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="ag-empty h-full">
      <span className="ag-empty-icon" aria-hidden="true">
        <FolderOpen />
      </span>
      <p className="ag-empty-title">{message}</p>
      {sub && <p>{sub}</p>}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="ag-empty h-full">
      <span className="ag-empty-icon text-amber-500" aria-hidden="true">
        <AlertTriangle />
      </span>
      <p className="ag-empty-title">Failed to load</p>
      <p className="font-mono text-xs">{message}</p>
    </div>
  );
}


function ToolsTab({ configKey, sensTools, serverName, tabs }: { configKey: string; sensTools?: any[]; serverName?: string; tabs: ReactNode }) {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Tool | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GatewayServersApi.tools(configKey)
      .then((data) => {
        if (!cancelled) setTools(Array.isArray(data) ? data : []);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(apiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configKey]);

  if (loading) {
    return <TabShell tabs={tabs}><LoadingState message="Loading tools…" /></TabShell>;
  }
  if (error) return <TabShell tabs={tabs}><ErrorState message={error} /></TabShell>;
  if (tools.length === 0) return <TabShell tabs={tabs}><EmptyState message="No tools registered" /></TabShell>;

  const filtered = priorityFilter(tools, search, (t) => t.name);

  return (
    <TabShell
      tabs={tabs}
      actions={<ServerSearch value={search} onChange={setSearch} placeholder={`Search ${tools.length} tools...`} />}
    >
      {filtered.length === 0 ? (
        <EmptyState message={`No tools matching "${search}"`} />
      ) : (
        <div className="ag-grid-table">
          <div className="ag-grid-head grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)_auto]">
            <div>Action Name</div>
            <div>Description</div>
            <div>Schema Context</div>
          </div>
          {filtered.map((tool, i) => {
            const count = Object.keys(tool.inputSchema?.properties ?? {}).length;
            return (
              <div key={tool.name + i} className="ag-grid-row grid-cols-[minmax(0,1.5fr)_minmax(0,2fr)_auto] items-center">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="ag-row-icon" aria-hidden="true">
                    <Wrench />
                  </span>
                  <span className="ag-row-title ag-mono" title={tool.name}>{tool.name}</span>
                  <GatewaySensitivityBadge value={capabilitySensitivity(sensTools, serverName, tool.name, "TOOL")} tag />
                </div>
                <div className="ag-row-text pr-8">{tool.description || "No description"}</div>
                <div>
                  <button
                    type="button"
                    onClick={() => setSelected(tool)}
                    className="ag-pill-button"
                  >
                    {count} {count === 1 ? "field" : "fields"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ToolFieldsDialog tool={selected} onClose={() => setSelected(null)} />
    </TabShell>
  );
}


function ResourcesTab({ configKey, sensTools, serverName, tabs }: { configKey: string; sensTools?: any[]; serverName?: string; tabs: ReactNode }) {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GatewayServersApi.resources(configKey)
      .then((data) => {
        if (!cancelled) setResources(Array.isArray(data) ? data : []);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(apiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configKey]);

  if (loading) {
    return <TabShell tabs={tabs}><LoadingState message="Loading resources…" /></TabShell>;
  }
  if (error) return <TabShell tabs={tabs}><ErrorState message={error} /></TabShell>;
  if (resources.length === 0) return <TabShell tabs={tabs}><EmptyState message="This server provides no resources" /></TabShell>;

  const filtered = priorityFilter(resources, search, (r) => r.name);

  return (
    <TabShell
      tabs={tabs}
      actions={<ServerSearch value={search} onChange={setSearch} placeholder={`Search ${resources.length} resources...`} />}
    >
      {filtered.length === 0 ? (
        <EmptyState message={`No resources matching "${search}"`} />
      ) : (
        <div className="ag-grid-table">
          <div className="ag-grid-head grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,2fr)_minmax(0,1.2fr)]">
            <div>Name</div>
            <div>URI</div>
            <div>Description</div>
            <div>MIME Type</div>
          </div>
          {filtered.map((res, i) => (
            <div key={(res.name ?? res.uri) + i} className="ag-grid-row grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,2fr)_minmax(0,1.2fr)] items-center">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="ag-row-icon" data-tone="teal" aria-hidden="true">
                  <Database />
                </span>
                <span className="ag-row-title ag-mono" title={res.name || res.uri}>{res.name || "—"}</span>
                <GatewaySensitivityBadge value={capabilitySensitivity(sensTools, serverName, res.name, "RESOURCE") || capabilitySensitivity(sensTools, serverName, res.uri, "RESOURCE")} tag />
              </div>
              <div className="ag-mono text-xs text-primary break-all">{res.uri ?? "—"}</div>
              <div className="ag-row-text pr-4">{res.description || "No description"}</div>
              <div className="ag-mono text-xs text-muted-foreground break-all">{res.mimeType ?? "—"}</div>
            </div>
          ))}
        </div>
      )}
    </TabShell>
  );
}


function PromptsTab({ configKey, sensTools, serverName, tabs }: { configKey: string; sensTools?: any[]; serverName?: string; tabs: ReactNode }) {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    GatewayServersApi.prompts(configKey)
      .then((data) => {
        if (!cancelled) setPrompts(Array.isArray(data) ? data : []);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(apiError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [configKey]);

  if (loading) {
    return <TabShell tabs={tabs}><LoadingState message="Loading prompts…" /></TabShell>;
  }
  if (error) return <TabShell tabs={tabs}><ErrorState message={error} /></TabShell>;
  if (prompts.length === 0) return <TabShell tabs={tabs}><EmptyState message="This server provides no prompts" /></TabShell>;

  const filtered = priorityFilter(prompts, search, (p) => p.name);

  return (
    <TabShell
      tabs={tabs}
      actions={<ServerSearch value={search} onChange={setSearch} placeholder={`Search ${prompts.length} prompts...`} />}
    >
      {filtered.length === 0 ? (
        <EmptyState message={`No prompts matching "${search}"`} />
      ) : (
        <div>
          {filtered.map((p, i) => {
            const args = p.arguments ?? [];
            const open = openIdx === i;
            return (
              <div key={p.name + i} className="ag-expander">
                <button
                  type="button"
                  className="ag-expander-trigger"
                  onClick={() => setOpenIdx(open ? null : i)}
                >
                  <span className="ag-row-icon" data-tone="violet" aria-hidden="true">
                    <MessageSquare />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="ag-row-title ag-mono" title={p.name}>{p.name}</span>
                      <GatewaySensitivityBadge value={capabilitySensitivity(sensTools, serverName, p.name, "PROMPT")} tag />
                    </div>
                    <div className="ag-row-text truncate">{p.description || "No description"}</div>
                  </div>
                  <span className="ag-chip" data-tone="muted">
                    {args.length} {args.length === 1 ? "arg" : "args"}
                  </span>
                  {open ? <ChevronDown /> : <ChevronRight />}
                </button>
                {open && args.length > 0 && (
                  <div className="ag-expander-body">
                    <span className="ag-section-label mb-3">Parameters Configuration</span>
                    <div className="ag-param-grid">
                      {args.map((a, j) => (
                        <div key={a.name + j} className="ag-field">
                          <div className="flex items-center gap-2">
                            <span className="ag-mono text-xs text-foreground">{a.name}</span>
                            <span className="ag-chip" data-tone={a.required ? "amber" : "muted"}>
                              {a.required ? "Required" : "Optional"}
                            </span>
                          </div>
                          {a.description && (
                            <div className="ag-row-text">{a.description}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </TabShell>
  );
}


function ServerDetailView({
  server,
  onBack,
  sens,
}: {
  server: ServerInfo;
  onBack: () => void;
  sens: EntitySensitivity;
}) {
  const displayName =
    server.serverName || server.configName || server.configKey;
  const version = server.version || server.serverVersion || "-";
  const currentServerName = server.configKey;

  const tabs = (
    <TabsList unstyled aria-label="Server capabilities" className="cea-tabs ag-tabs">
      <TabsTrigger unstyled value="tools" className="cea-tab">
        <Wrench className="shrink-0" />
        Tools
        {(server.toolCount ?? 0) > 0 && <span className="ag-tab-count">{server.toolCount}</span>}
      </TabsTrigger>
      <TabsTrigger unstyled value="resources" className="cea-tab">
        <Database className="shrink-0" />
        Resources
        {(server.resourceCount ?? 0) > 0 && <span className="ag-tab-count">{server.resourceCount}</span>}
      </TabsTrigger>
      <TabsTrigger unstyled value="prompts" className="cea-tab">
        <MessageSquare className="shrink-0" />
        Prompts
        {(server.promptCount ?? 0) > 0 && <span className="ag-tab-count">{server.promptCount}</span>}
      </TabsTrigger>
    </TabsList>
  );

  return (
    <>
      <div className="ag-card ag-detail-header">
        <div className="ag-detail-identity">
          <button
            type="button"
            onClick={onBack}
            className="ag-back-button"
            title="Back to Servers"
            aria-label="Back to Servers"
          >
            <ArrowLeft />
          </button>
          <span className="ag-detail-avatar" aria-hidden="true">
            <Server />
          </span>
          <div className="min-w-0">
            <h1 className="ag-detail-title">
              <ServerNameDisplay name={displayName} />
            </h1>
            <div className="ag-detail-meta">
              <span>MCP Servers</span>
              <span aria-hidden="true">/</span>
              <code className="ag-inline-code">{server.configKey}</code>
            </div>
          </div>
        </div>
        <div className="ag-toolbar-group">
          <ConnectionBadge active={server.active} />
          {version && version !== "-" && (
            <span className="ag-chip" data-tone="muted">v{version}</span>
          )}
        </div>
      </div>

      <Tabs defaultValue="tools" className="ag-tabs-root">
        <TabsContent value="tools" className="ag-panel mt-0">
          <ToolsTab configKey={server.configKey} sensTools={sens.tools} serverName={currentServerName} tabs={tabs} />
        </TabsContent>
        <TabsContent value="resources" className="ag-panel mt-0">
          <ResourcesTab configKey={server.configKey} sensTools={sens.tools} serverName={currentServerName} tabs={tabs} />
        </TabsContent>
        <TabsContent value="prompts" className="ag-panel mt-0">
          <PromptsTab configKey={server.configKey} sensTools={sens.tools} serverName={currentServerName} tabs={tabs} />
        </TabsContent>
      </Tabs>
    </>
  );
}


function ServerCard({
  server,
  onSelect,
  sens,
}: {
  server: ServerInfo;
  onSelect: (s: ServerInfo) => void;
  sens: EntitySensitivity;
}) {
  const displayName =
    server.serverName || server.configName || server.configKey;
  const version = server.version || server.serverVersion;
  // backend /api/mcp/servers exposes connectedAt (not lastActivity)
  const connectedLabel = server.connectedAt ? timeAgo(server.connectedAt) : "—";

  return (
    <div
      className="ag-entity ag-clickable"
      onClick={() => onSelect(server)}
    >
      <div className="ag-entity-head">
        <div className="ag-entity-identity">
          <span className="ag-entity-avatar" aria-hidden="true">
            <Server />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h3 className="ag-entity-name" title={displayName}>{displayName}</h3>
              <GatewaySensitivityBadge value={(sens.serversByName || {})[server.configKey] || (sens.serversByName || {})[displayName]} tag />
            </div>
            <div className="ag-entity-sub">
              <span className="ag-status" data-tone={server.active ? "success" : "danger"}>
                <span className="ag-status-dot" aria-hidden="true" />
                {server.active ? "Connected" : "Disconnected"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="ag-entity-body">
        <div className="ag-server-metrics ag-metrics-3">
          {[
            { label: "Tools", value: server.toolCount ?? 0, tone: "primary" },
            { label: "Resources", value: server.resourceCount ?? 0, tone: "teal" },
            { label: "Prompts", value: server.promptCount ?? 0, tone: "violet" },
          ].map((cap) => (
            <div key={cap.label} className="ag-metric" data-tone={cap.value > 0 ? cap.tone : "muted"}>
              <span className="ag-metric-label">{cap.label}</span>
              <span className="ag-metric-value text-lg">{cap.value}</span>
            </div>
          ))}
        </div>

        <div className="ag-kv-list">
          <div className="ag-kv">
            <span>Config Key</span>
            <span className="ag-mono text-primary">{server.configKey}</span>
          </div>
          <div className="ag-kv">
            <span>Connected</span>
            <span>{connectedLabel}</span>
          </div>
          {version && (
            <div className="ag-kv">
              <span>Server Version</span>
              <span className="ag-mono" title={version}>{version}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function GatewayServersPage() {
  const { toast } = useToast();
  const [servers, setServers] = useState<ServerInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedServer, setSelectedServer] = useState<ServerInfo | null>(null);
  const [serverSearch, setServerSearch] = useState("");
  const [sens, setSens] = useState<EntitySensitivity>({ serversByName: {}, tools: [] });

  const loadServers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, sensData] = await Promise.all([
        GatewayServersApi.list(),
        GatewayPostProcessorApi.entitySensitivity().catch(() => ({})),
      ]);
      setSens(sensData || { serversByName: {}, tools: [] });
      const parsed: ServerInfo[] = Object.entries(data || {}).map(
        ([configKey, info]: [string, any]) => ({
          ...info,
          configKey,
          active: info.status === "connected" || info.status === "CONNECTED",
          version: info.serverVersion || info.version || "",
          resourceCount: info.resourceCount || 0,
          promptCount: info.promptCount || 0,
          toolCount: info.toolCount || 0,
        })
      );
      setServers(parsed);
    } catch (e: any) {
      setError(apiError(e));
      toast({
        title: "Failed to load servers",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleSelectServer = (server: ServerInfo) => {
    setSelectedServer(server);
  };

  const handleBack = () => {
    setSelectedServer(null);
  };

  const connectedCount = servers.filter((s) => s.active).length;
  const disconnectedCount = servers.filter((s) => !s.active).length;

  if (selectedServer) {
    return (
      <div className="ag-page">
        <ServerDetailView server={selectedServer} onBack={handleBack} sens={sens} />
      </div>
    );
  }

  return (
    <div className="ag-page">
      <div className="ag-card ag-toolbar">
        <div className="ag-toolbar-group">
          {!loading && !error && (
            <span className="ag-summary">
              <Server aria-hidden="true" />
              <span>
                <strong>{servers.length}</strong> server{servers.length !== 1 ? "s" : ""}
              </span>
            </span>
          )}
          {!loading && !error && servers.length > 0 && (
            <>
              <span className="ag-chip" data-tone="success">
                <CheckCircle2 />
                {connectedCount} connected
              </span>
              {disconnectedCount > 0 && (
                <span className="ag-chip" data-tone="danger">
                  <XCircle />
                  {disconnectedCount} disconnected
                </span>
              )}
            </>
          )}
        </div>
        <div className="ag-toolbar-group">
          {!loading && !error && servers.length > 0 && (
            <ServerSearch value={serverSearch} onChange={setServerSearch} placeholder="Search servers..." />
          )}
          <Button
            variant="outline"
            className="ag-button no-default-hover-elevate"
            onClick={loadServers}
            disabled={loading}
          >
            {loading ? (
              <span className="ag-spinner animate-spin" aria-hidden="true" />
            ) : (
              <RotateCw />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <div className="ag-card ag-content">
        <div className="ag-scroll">
          {loading && <LoadingState message="Loading servers…" />}

          {!loading && error && (
            <div className="ag-empty h-full">
              <span className="ag-empty-icon text-amber-500" aria-hidden="true">
                <AlertTriangle />
              </span>
              <p className="ag-empty-title">Failed to load servers</p>
              <p className="font-mono text-xs">{error}</p>
              <Button variant="outline" className="ag-button no-default-hover-elevate mt-2" onClick={loadServers}>
                Try Again
              </Button>
            </div>
          )}

          {!loading && !error && servers.length === 0 && (
            <div className="ag-empty h-full">
              <span className="ag-empty-icon" aria-hidden="true">
                <Server />
              </span>
              <p className="ag-empty-title">No MCP servers connected</p>
              <p>Go to Configuration to add and connect servers</p>
            </div>
          )}

          {!loading && !error && servers.length > 0 && (() => {
            const filteredServers = (() => {
              if (!serverSearch.trim()) return servers;
              const q = serverSearch.toLowerCase().trim();
              const startsWith: typeof servers = [];
              const contains: typeof servers = [];
              for (const s of servers) {
                const name = (s.serverName || s.configName || "").toLowerCase();
                const fields = [name, (s.configKey || "").toLowerCase(), (s.version || "").toLowerCase()];
                if (name.startsWith(q)) startsWith.push(s);
                else if (fields.some((f) => f.includes(q))) contains.push(s);
              }
              return [...startsWith, ...contains];
            })();
            return filteredServers.length === 0 ? (
              <EmptyState message={`No servers matching "${serverSearch}"`} />
            ) : (
              <div className="ag-server-list">
                {filteredServers.map((server) => (
                  <ServerCard
                    key={server.configKey}
                    server={server}
                    onSelect={handleSelectServer}
                    sens={sens}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
